<template>
  <div aria-hidden="true" style="height: 100%">
    <mds-section
      v-if="exhibit"
      ref="exhibit"
      :border="showTitle ? 'primary' : 'none'"
      :class="['pf-exhibit', exhibitClass]"
      :heading-level="headingLevel"
      :size="headingLevel"
      :title="showTitle"
      bold
    >
      <p
        v-if="!configuration && caption"
        :style="!title && 'margin-bottom: 0;'"
        class="pf-exhibit--caption"
      >
        {{ caption }}
      </p>
      <div v-else />
      <component
        v-if="layout"
        ref="layout"
        :is="exhibit.type"
        :height="innerHeight"
        :layout="layout"
        :preview="preview"
        :width="innerWidth"
        :configuration="configuration"
        v-bind="layoutProps"
        v-on="configuration ? annotationListeners : null"
      />
      <div ref="footer" v-if="showFooter">
        <span v-if="!configuration && asOf" class="pf-date">
          {{ dateLabel }}
        </span>
        <p v-if="!configuration && source" :style="!source && 'margin-top: 0;'" class="pf-source">
          {{ source }}
        </p>
        <div>
          <p
            v-if="!configuration && footnote"
            :style="!footnote && 'margin-top: 0;'"
            class="pf-footnote"
          >
            {{ footnote }}
          </p>
        </div>
      </div>
    </mds-section>
  </div>
</template>

<script>
  import MdsSection from '@mds/section';
  import parseDecimal from '@/utils/parseDecimal';
  import revive from '@/utils/revive';
  import serialize from '@/utils/serialize';

  export default {
    components: {
      MdsSection,
    },
    computed: {
      exhibitClass() {
        const obj = {};

        obj['pf-exhibit_headless'] = !this.title && !this.configuration;
        obj['pf-exhibit_config'] = this.configuration;

        return obj;
      },
      // TODO provide tool props via a mixin?
      annotationListeners() {
        const vm = this;
        return {
          ...this.$listeners,
          pfdeselectannotation() {
            vm.$emit('pfdeselectannotation');
          },
          pfmousedown(ev) {
            vm.$emit('pfmousedown', ev);
          },
          pfmouseup() {
            vm.$emit('pfmouseup');
          },
          pfmousemove(ev) {
            vm.$emit('pfmousemove', ev);
          },
          pfannotationselected(arg) {
            vm.$emit('pfannotationselected', arg);
          },
          pfselectionplot(arg) {
            vm.$emit('pfselectionplot', arg);
          },
          pfcleartoolbar() {
            vm.$emit('pfcleartoolbar');
          },
          pfloadtoolbar(arg) {
            vm.$emit('pfloadtoolbar', arg);
          },
          pflabelupdate(arg) {
            vm.$emit('pflabelupdate', arg);
          },
          pfupdateconfig(arg) {
            vm.$emit('pfupdateconfig', arg);
          },
          pfsavelayout(arg) {
            vm.$emit('pfsavelayout', JSON.stringify(arg, serialize));
          },
        };
      },
      asOf() {
        if (this.withCopy) {
          return this.exhibit.asOf;
        }
        return '';
      },
      dateLabel() {
        if (this.asOf.length > 0) {
          const date = new Date(this.asOf.replace(/-/g, '/'));
          return `Data as of ${date.toLocaleString('en-us', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}`;
        }

        return '';
      },
      caption() {
        if (this.withCopy) {
          return this.exhibit.caption;
        }
        return '';
      },
      annotations() {
        return this.exhibit.layout.annotations ?? {};
      },
      footnote() {
        if (this.withCopy) {
          return this.exhibit.footnote;
        }
        return '';
      },
      source() {
        if (this.withCopy) {
          return this.exhibit.sourceline;
        }
        return '';
      },
      showTitle() {
        return !this.configuration && this.title ? this.title : '';
      },
      showFooter() {
        return !this.configuration && (this.source || this.footnote);
      },
      headerTitle() {
        return this.$refs.exhibit?.$el.childNodes[0]?.children[0];
      },
      headingLevel() {
        return Number(this.settings.heading) || 1;
      },
      layout() {
        return this.exhibit.layout && revive(this.exhibit.layout);
      },
      layoutProps() {
        return {
          configuration: this.configuration,
          selectedAnnotation: this.selectedAnnotation,
          clicks: this.clicks,
          annotationsOff: this.annotationsOff,
        };
      },
      title() {
        if (this.withCopy && this.exhibit) {
          return this.exhibit.title;
        }
        return '';
      },
      withCopy() {
        const { copy } = this.settings;
        return copy && copy !== 'false';
      },
      layoutRefs() {
        return this.$refs.layout?.$refs;
      },
    },
    created() {
      if (this.exhibit) {
        this.overrideSettings();
      }
    },
    data() {
      return {
        innerHeight: 600,
        innerWidth: 948,
        settings: {},
        titleRemoved: false,
      };
    },
    methods: {
      overrideSettings() {
        // inspect the URL for query-string parameters
        const { searchParams } = new URL(window.location.href);
        // valid props are read from the exhibit configuration
        Object.keys(this.exhibit.settings || {}).forEach((param) => {
          this.$set(
            this.settings,
            param,
            (searchParams.has(param) && searchParams.get(param)) || this.exhibit.settings[param]
          );
        });
      },
      resizeLayout() {
        const { exhibit, layout, footer } = this.$refs;

        if (this.fixedDimensions) {
          return;
        }

        if (exhibit) {
          const exhibitCompStyle = getComputedStyle(exhibit.$el);
          // the whole exhibit
          const exhibitBox = exhibit.$el.getBoundingClientRect();
          // the footer (inc. source, footnote)
          const footerBox = footer ? footer.getBoundingClientRect() : { height: 0 };
          // the layout (i.e. plot)
          const layoutBox = layout.$el.getBoundingClientRect();

          this.innerHeight =
            // the total height of the exhibit
            parseDecimal(exhibitCompStyle.height) -
            // minus
            (layoutBox.top - exhibitBox.top + footerBox.height);

          this.innerWidth =
            parseDecimal(exhibitBox.width) === 0 ? this.innerWidth : parseDecimal(exhibitBox.width);
        }
      },
    },
    mounted() {
      if (this.fixedDimensions) {
        this.innerHeight = this.fixedDimensions.height;
        this.innerWidth = this.fixedDimensions.width;
      } else {
        // observe resize events from the parent
        new ResizeObserver(this.resizeLayout.bind(this)).observe(this.$el.parentElement);
        // set the initial dimensions, if the exhibit is already defined
        if (this.exhibit) {
          this.resizeLayout();
        }
      }
    },
    name: 'PfExhibit',
    props: {
      annotationsOff: {
        type: Boolean,
        default: false,
      },
      configuration: Boolean,
      exhibit: Object,
      selectedAnnotation: Object,
      selectedPlot: Object,
      element: Object,
      fixedDimensions: Object,
      clicks: Number,
      preview: {
        type: Boolean,
        default: false,
      },
    },
    watch: {
      exhibit: {
        deep: true,
        handler(a, b) {
          if (a !== b) {
            this.overrideSettings();
            requestAnimationFrame(() => this.resizeLayout());
          } else {
            this.resizeLayout();
          }
        },
      },
      title(val) {
        if (!this.headerTitle || (this.headerTitle && !this.headerTitle.textContent)) {
          this.titleRemoved = true;
        }

        if (this.titleRemoved && this.headerTitle && this.headerTitle.textContent) {
          this.headerTitle.textContent = val.trim();
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '@mds/constants';
  @import '@mds/typography';

  section.pf-exhibit {
    background-color: #fff;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;

    &--caption {
      @include mds-body-text-l();

      margin-top: 0;
    }

    & > div:nth-child(2) {
      display: flex;
      flex-direction: column;
      flex-grow: 2;
    }

    & > div:nth-child(2) > div.pf-chart-element {
      flex-grow: 2;
    }

    .pf-footnote {
      white-space: pre-line;
      display: inline-block;
    }

    &_config {
      .pf-title,
      .pf-footnote {
        width: 100%;
      }

      .pf-title {
        padding-bottom: 5px;
      }

      .pf-title,
      .pf-axis-label {
        &:hover {
          background: rgba($mds-interactive-color-primary-default, 0.25);
          border-radius: 5px;
        }

        span {
          display: inline-block;
          width: 100%;
        }
      }

      .pf-exhibit--caption,
      .pf-footnote,
      .pf-source {
        &:hover {
          background: rgba($mds-interactive-color-primary-default, 0.25);
          border-radius: 5px;
        }
      }
    }
  }

  .pf-exhibit--caption {
    margin-top: 2px;
  }

  .pf-footnote,
  .pf-source,
  .pf-date {
    @include mds-body-text-s();

    align-items: flex-end;
    color: $mds-text-color-secondary;
    display: flex;
    margin-bottom: 0;
    margin-top: 0;
    padding-top: $mds-space-1-x;
  }

  .pf-footnote {
    padding-top: 4px;
  }

  .pf-source {
    max-lines: 1;
    display: inline-flex;
    min-width: 60%;
  }

  .pf-date {
    display: inline-flex;

    &::after {
      color: #ccc;
      content: '•';
      padding-left: 4px;
      padding-right: 4px;
    }
  }
</style>

<style module lang="scss">
  // MDS overrides only
  @import '@mds/constants';
  @import '@mds/typography';

  :global(.pf-exhibit_headless) {
    .mds-section__header-container {
      display: none;
    }
  }

  :global(.pf-exhibit) {
    .mds-section__header-container,
    .mds-section__header-container.mds-section--primary {
      margin-bottom: $mds-space-three-quarter-x;
    }
  }
</style>
